import { atom, selector } from 'recoil';

import { Token } from '@stripe/stripe-js';

import { accountQueryState, authenticatedState } from '../Account/atoms';
import { AccountQuery } from '../Account/graphql';
import {
	GiftCardPaymentInput,
	Maybe,
	PercPickupLocation,
	TerminalFieldsFragment
} from '../graphql';
import { isStoreGlobalState, storeState } from '../Store/atoms';
import { isAsapAvailable } from '../Store/utils/isAsapAvailable';
import { defined } from '../utils/defined';
import { emailValidator } from '../utils/emailValidator';
import { getNow } from '../utils/getNow';
import { notEmptyValidator } from '../utils/notEmptyValidator';
import { persistAtom } from '../utils/persistAtom';
import { validatePhone } from '../utils/validatePhone';
import { SendOrderError } from './hooks/useSendOrder';
import { CheckoutErrors, State } from './types';
import { GetTokenError } from './utils/getStripeToken';
import { validator } from './utils/validator';

export const giftCardPaymentsState = atom<GiftCardPaymentInput[]>({
	key: 'checkout_giftCardPayments',
	default: []
});

export const submissionAttemptedState = atom<boolean>({
	key: 'checkout_submissionAttempted',
	default: false
});

export const profileStepSubmissionAttemptedState = atom<boolean>({
	key: 'checkout_profileStepSubmissionAttempted',
	default: false
});

export const guestNameState = atom<string>({
	key: 'checkout_guestName',
	default: '',
	effects_UNSTABLE: [persistAtom]
});

export const guestNameTouchedState = atom<boolean>({
	key: 'checkout_guestNameTouched',
	default: false
});

export const guestNameValidationState = selector<string | undefined>({
	key: 'checkout_guestNameValidation',
	get({ get }) {
		const profileFirstName = get(profileFirstNameState);
		const guestName = get(guestNameState);

		if (profileFirstName && profileFirstName !== '') return;

		if (guestName !== '') return;

		return 'Name is required.';
	}
});

export const guestEmailState = atom<string>({
	key: 'checkout_guestEmail',
	default: '',
	effects_UNSTABLE: [
		arg => {
			setTimeout(() => {
				persistAtom(arg);
			});
		}
	]
});

export const guestEmailTouchedState = atom<boolean>({
	key: 'checkout_guestEmailTouched',
	default: false
});

export const guestEmailValidationState = selector<string | undefined>({
	key: 'checkout_guestEmailValidation',
	get({ get }) {
		const authenticated = get(authenticatedState);
		const guestEmail = get(guestEmailState);

		if (authenticated) return;

		return emailValidator(guestEmail);
	}
});

export const guestEmailValidationWithTouchedState = validator(
	guestEmailValidationState,
	profileStepSubmissionAttemptedState
);

export const guestPhoneState = atom<string>({
	key: 'checkout_guestPhone',
	default: '',
	effects_UNSTABLE: [persistAtom]
});

export const guestPhoneTouchedState = atom<boolean>({
	key: 'checkout_guestPhoneTouched',
	default: false
});

export const guestPhoneValidationState = selector<string | undefined>({
	key: 'checkout_guestPhoneValidation',
	get({ get }) {
		const profilePhone = get(profilePhoneState);
		const guestPhone = get(guestPhoneState);

		if (profilePhone && validatePhone(profilePhone)) return;

		if (guestPhone === '') return 'Phone is required.';

		if (guestPhone.length !== 10 || !validatePhone(guestPhone))
			return 'Phone number must be 10 digits. Example: 5553334567';

		return;
	}
});

export const guestPhoneValidationWithTouchedState = validator(
	guestPhoneValidationState,
	profileStepSubmissionAttemptedState
);

export const sessionProfileState = selector<AccountQuery['session']['profile']>(
	{
		key: 'checkout_sessionProfile',
		get({ get }) {
			const res = get(accountQueryState);

			return res?.data.session.profile;
		}
	}
);

export const profileFirstNameState = selector<string | undefined>({
	key: 'checkout_profileFirstName',
	get({ get }) {
		return get(sessionProfileState)?.firstName;
	}
});

export const profileEmailState = selector<Maybe<string> | undefined>({
	key: 'checkout_profileEmail',
	get({ get }) {
		return get(sessionProfileState)?.email;
	}
});

export const profilePhoneState = selector<Maybe<string> | undefined>({
	key: 'checkout_profilePhone',
	get({ get }) {
		return get(sessionProfileState)?.phone?.replaceAll(/[^0-9]/g, '');
	}
});

export const pickupNameState = selector<string | undefined>({
	key: 'checkout_pickupName',
	get({ get }) {
		const firstName = get(profileFirstNameState);

		if (firstName && firstName !== '') {
			return firstName;
		}

		const guestName = get(guestNameState);
		const guestNameValidation = get(guestNameValidationState);

		if (defined(guestNameValidation)) return;

		return guestName;
	}
});

export const pickupEmailState = selector<string | undefined>({
	key: 'checkout_pickupEmail',
	get({ get }) {
		const email = get(profileEmailState);
		const guestEmail = get(guestEmailState);
		const guestEmailValidation = get(guestEmailValidationState);

		if (email) {
			return email;
		}

		if (defined(guestEmailValidation)) return;

		return guestEmail;
	}
});

export const pickupPhoneState = selector<string | undefined>({
	key: 'checkout_pickupPhone',
	get({ get }) {
		const phone = get(profilePhoneState);

		if (phone && validatePhone(phone)) {
			return phone;
		}

		const guestPhone = get(guestPhoneState);
		const guestPhoneValidation = get(guestPhoneValidationState);

		if (defined(guestPhoneValidation)) return;

		return guestPhone;
	}
});

export const isProfileStepValidState = selector<boolean>({
	key: 'checkout_isProfileStepValid',
	get({ get }) {
		const pickupName = get(pickupNameState);
		const pickupEmail = get(pickupEmailState);
		const pickupPhone = get(pickupPhoneState);

		return Boolean(pickupName && pickupEmail && pickupPhone);
	}
});

export const canSkipProfileStepState = selector<boolean>({
	key: 'checkout_canSkipProfileStep',
	get({ get }) {
		const profileFirstName = get(profileFirstNameState);
		const profileEmail = get(profileEmailState);
		const profilePhone = get(profilePhoneState);

		return Boolean(profileFirstName && profileEmail && profilePhone);
	}
});

export const storeTerminalsState = selector<TerminalFieldsFragment[]>({
	key: 'checkout_storeTerminalsState',
	get({ get }) {
		const store = get(storeState);

		return store?.terminals ?? [];
	}
});

export const driveThruConnectedState = selector<boolean>({
	key: 'checkout_driveThruConnected',
	get({ get }) {
		const terminals = get(storeTerminalsState);

		return terminals.some(
			terminal => terminal.status === 'CONNECTED' && terminal.termId === 3
		);
	}
});

export const counterConnectedState = selector<boolean>({
	key: 'checkout_counterConnected',
	get({ get }) {
		const terminals = get(storeTerminalsState);

		return terminals.some(
			terminal => terminal.status === 'CONNECTED' && terminal.termId === 2
		);
	}
});

export const driveThruPickupState = selector<boolean>({
	key: 'checkout_driveThruPickup',
	get({ get }) {
		const store = get(storeState);
		const driveThruConnected = get(driveThruConnectedState);

		return !!store?.driveThruPickup && driveThruConnected;
	}
});

export const inStorePickupState = selector<boolean>({
	key: 'checkout_inStorePickup',
	get({ get }) {
		const store = get(storeState);
		const counterConnected = get(counterConnectedState);

		return !!store?.inStorePickup && counterConnected;
	}
});

export const parkingLotPickupState = selector<boolean>({
	key: 'checkout_parkingLotPickup',
	get({ get }) {
		const store = get(storeState);
		const counterConnected = get(counterConnectedState);

		return !!store?.parkingLotPickup && counterConnected;
	}
});

export const pickupLocationsState = selector<PercPickupLocation[]>({
	key: 'checkout_pickupLocations',
	get({ get }) {
		const driveThruPickup = get(driveThruPickupState);
		const inStorePickup = get(inStorePickupState);
		const parkingLotPickup = get(parkingLotPickupState);

		const pickupLocations: PercPickupLocation[] = [];

		if (driveThruPickup) pickupLocations.push('DRIVE_THRU');
		if (inStorePickup) pickupLocations.push('IN_STORE');
		if (parkingLotPickup) pickupLocations.push('PARKING_LOT');

		return pickupLocations;
	}
});

export const defaultPickupLocationState = selector<PercPickupLocation>({
	key: 'checkout_pickupLocation__default',
	get({ get }) {
		const driveThruPickup = get(driveThruPickupState);
		const parkingLotPickup = get(parkingLotPickupState);

		if (driveThruPickup) return 'DRIVE_THRU';
		if (parkingLotPickup) return 'PARKING_LOT';

		return 'IN_STORE';
	}
});

export const pickupLocationState = atom<PercPickupLocation>({
	key: 'checkout_pickupLocation',
	default: defaultPickupLocationState
});

export const isStoreAvailableState = selector<boolean>({
	key: 'checkout_isStoreAvailable',
	get({ get }) {
		const pickupLocations = get(pickupLocationsState);
		const store = get(storeState);
		let asapAvailable = false;
		if (store) {
			asapAvailable = isAsapAvailable(store, getNow());
		}

		return pickupLocations.length > 0 && asapAvailable;
	}
});

export const mustChangeStoreState = selector<boolean>({
	key: 'checkout_mustChangeStore',
	get({ get }) {
		const isStoreAvailable = get(isStoreAvailableState);
		const isStoreGlobal = get(isStoreGlobalState);

		return !isStoreAvailable || isStoreGlobal;
	}
});

export const showPickupMethodState = selector<boolean>({
	key: 'checkout_showPickupMethod',
	get({ get }) {
		const mustChangeStore = get(mustChangeStoreState);

		if (mustChangeStore) return false;

		const parkingLotPickup = get(parkingLotPickupState);

		if (parkingLotPickup) return true;

		const pickupLocations = get(pickupLocationsState);

		return pickupLocations.length > 1;
	}
});

export const showParkingLotInDrawerState = selector<boolean>({
	key: 'checkout_showParkingLotInDrawer',
	get({ get }) {
		const pickupLocations = get(pickupLocationsState);

		return pickupLocations.length > 1;
	}
});

export const canSkipPickupStepState = selector<boolean>({
	key: 'checkout_canSkipPickupStep',
	get({ get }) {
		const showPickupMethod = get(showPickupMethodState);
		const mustChangeStore = get(mustChangeStoreState);

		return !showPickupMethod && !mustChangeStore;
	}
});

export const stripeTokenState = atom<Token | undefined>({
	key: 'checkout_stripeToken',
	default: undefined
});

export const pickupMethodSubmissionAttemptedState = atom<boolean>({
	key: 'checkout_pickupMethodSubmissionAttempted',
	default: false
});

export const carMakeState = atom<string | undefined>({
	key: 'checkout_carMake',
	default: undefined,
	effects_UNSTABLE: [persistAtom]
});

export const carMakeValidationState = validator(
	notEmptyValidator('Car make', carMakeState),
	pickupMethodSubmissionAttemptedState
);

export const carModelState = atom<string | undefined>({
	key: 'checkout_carModel',
	default: undefined,
	effects_UNSTABLE: [persistAtom]
});

export const carModelValidationState = validator(
	notEmptyValidator('Car model', carModelState),
	pickupMethodSubmissionAttemptedState
);

export const carColorState = atom<string | undefined>({
	key: 'checkout_carColor',
	default: undefined,
	effects_UNSTABLE: [persistAtom]
});

export const carColorValidationState = validator(
	notEmptyValidator('Car color', carColorState),
	pickupMethodSubmissionAttemptedState
);

export const isPickupMethodValidState = selector<boolean>({
	key: 'checkout_isPickupMethodValid',
	get({ get }) {
		const pickupLocation = get(pickupLocationState);

		if (pickupLocation !== 'PARKING_LOT') return true;

		return ![
			carMakeValidationState,
			carModelValidationState,
			carColorValidationState
		]
			.map(get)
			.map(validationState => validationState.validation)
			.some(defined);
	}
});

export const isCheckoutLoadingState = atom<boolean>({
	key: 'checkout_isCheckoutLoading',
	default: false
});

export const stripeErrorState = atom<GetTokenError | undefined>({
	key: 'checkout_stripeError',
	default: undefined
});

export const sendOrderErrorState = atom<SendOrderError | undefined>({
	key: 'checkout_sendOrderError',
	default: undefined
});

export const checkoutErrorsState = selector<CheckoutErrors>({
	key: 'checkout_error',
	get({ get }) {
		const stripeError = get(stripeErrorState);
		const sendOrderError = get(sendOrderErrorState);

		return [stripeError, sendOrderError];
	}
});

export const checkoutState = selector<State>({
	key: 'checkout_checkoutState',
	dangerouslyAllowMutability: true,
	get({ get }) {
		const authenticated = get(authenticatedState);
		const balanceDue = 0;
		const giftCardPayments = get(giftCardPaymentsState);
		const pickupLocation = get(pickupLocationState);
		const guestEmail = get(guestEmailState);
		const pickupName = get(pickupNameState);
		const phone = get(pickupPhoneState);
		const carMake = get(carMakeState);
		const carColor = get(carColorState);
		const carModel = get(carModelState);

		return {
			authenticated,
			balanceDue,
			giftCardPayments,
			pickupLocation,
			guestEmail,
			pickupName: pickupName || '',
			phone: phone || '',
			isScheduled: false,
			carMake,
			carModel,
			carColor
		};
	}
});
